// src/themes/TableStyles.js
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircleCheck,
  faCircleMinus,
  faCircle,
} from "@fortawesome/free-solid-svg-icons";
import { Box, HStack, Text } from "@chakra-ui/react";

// Custom styles for DataTable
export const tableCustomStyles = {
  table: {
    style: {
      borderRadius: "10px", // กำหนดมุมโค้งให้ตาราง
      overflow: "hidden", // ป้องกันเนื้อหาล้น
      border: "1px solid #e2e8f0", // เพิ่มขอบเพื่อให้เห็นมุมโค้งชัดเจน
    },
  },
  header: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "18px",
      fontWeight: "bold",
    },
  },
  headCells: {
    style: {
      backgroundColor: "#6BA9B4",
      color: "white",
      fontSize: "16px",
      fontWeight: "semibold",
      padding: "8px",
      lineHeight: "2",
      "&:first-of-type": {
        borderTopLeftRadius: "10px", // มุมบนซ้าย
      },
      "&:last-of-type": {
        borderTopRightRadius: "10px", // มุมบนขวา
      },
    },
  },
  cells: {
    style: {
      paddingLeft: "8px",
      paddingRight: "8px",
      lineHeight: "2", // ปรับ line-height เพื่อให้ข้อความไม่ถูกตัด
      display: "flex",
      alignItems: "center", // จัดข้อความให้อยู่กึ่งกลางในแนวตั้ง
      padding: "10px",
      fontSize: "14px",
      "&:first-of-type": {
        borderBottomLeftRadius: "10px", // มุมล่างซ้าย (สำหรับแถวสุดท้าย)
      },
      "&:last-of-type": {
        borderBottomRightRadius: "10px", // มุมล่างขวา (สำหรับแถวสุดท้าย)
      },
    },
  },
  rows: {
    style: {
      minHeight: "35px", // เพิ่มความสูงของแถว
      paddingTop: "5px", // เพิ่ม padding ด้านบน
      paddingBottom: "5px", // เพิ่ม padding ด้านล่าง
      "&:hover": {
        backgroundColor: "#f5f5f5",
      },
    },
  },
};

// Map สถานะภาษาอังกฤษเป็นภาษาไทย
const statusLabels = {
  Pending: "รอดำเนินการ",
  Pending: "รอการอนุมัติ",
  Confirmed: "ยืนยันแล้ว",
  Completed: "เสร็จสิ้น",
  Cancelled: "ยกเลิก",
  Approved: "อนุมัติ",
  Rejected: "ปฏิเสธ",
  Overdue: "เกินกำหนด",
  "New Reservation": "ใบจองใหม่", // เพิ่มสำหรับ VaccineReservationList ถ้ามีการใช้
};

// ฟังก์ชันกำหนดสไตล์สถานะ
export const getStatusStyle = (status) => {
  switch (status) {
    case "Pending":
    case "รอดำเนินการ" :
    case "รอการอนุมัติ" :
      return { bg: "#f9e9cf", color: "#D97706", border: "#f1a144" };
    case "Confirmed":
      return { bg: "#c8fdd8", color: "#2d8a70", border: "#52bc9e" };
    case "Completed":
      return { bg: "#a5c1ef", color: "#4c6284", border: "#6f89b3" };
    case "Cancelled":
      return { bg: "#ffd0d0", color: "#bd4c4c", border: "#ff7373" };
    case "Approved":
      case "อนุมัติ" :
      case "อนุมัติการจอง" :
      case "ยืนยันการจอง":
      return { bg: "#d1fad2", color: "#5b955d", border: "#76bf78" };
    case "Rejected":
      return { bg: "#FFEDD5", color: "#e1a050", border: "#f58d0d" };
    case "Overdue":
      return { bg: "#d7cff9", color: "#705ebd", border: "#8d78e3" };
    case "New":
    case "ใบจองใหม่":
      return { bg: "#bbd7f1", color: "#2a72b4", border: "#58a5ec" };
    case "Active":
      return { bg: "#d1f5cb", color: "#5e9144", border: "#78ad5e" };
    case "Inactive":
      return { bg: "#d1d1d1", color: "#909090", border: "#ababab" };
    default:
      return { bg: "#d1d1d1", color: "#d1d1d1", border: "#ababab" }; // Default ถ้าสถานะไม่ตรง
  }
};

// ฟังก์ชันสำหรับแสดงสถานะเป็นภาษาไทยในตาราง
export const StatusBadge = ({ status }) => (
  <Box
    bg={getStatusStyle(status).bg}
    color={getStatusStyle(status).color}
    borderColor={getStatusStyle(status).border}
    borderWidth={1}
    borderRadius="full"
    px={3}
    py={1}
    textAlign="center"
    fontSize="md"
    fontWeight="normal"
    display="inline-block"
    width="100px"
  >
    {statusLabels[status] || status}{" "}
    {/* แสดง label ภาษาไทย ถ้าไม่มีใช้ status เดิม */}
  </Box>
);

// สีสถานะของใบจองวัคซีน ใช้ร่วมกันระหว่างหน้ารายการและหน้ารายละเอียด
export const reservationStatusConfig = {
  ใบจองใหม่: { color: "blue.500" },
  ยืนยันการจอง: { color: "green.500" },
  รอการอนุมัติ: { color: "orange.500" },
  พร้อมรับวัคซีน: { color: "pink.500" },
  ยกเลิก: { color: "red.500" },
  จัดส่งแล้ว: { color: "purple.500" },
};

// แสดงสถานะใบจองเป็นจุดสีพร้อมข้อความ
export const ReservationStatusDot = ({ status, fontSize }) => {
  const config = reservationStatusConfig[status] || { color: "gray.500" };

  return (
    <HStack spacing={2} align="center" minW={0}>
      <Box color={config.color} display="flex" alignItems="center">
        <FontAwesomeIcon icon={faCircle} size="xs" />
      </Box>
      <Text fontSize={fontSize} noOfLines={1}>
        {status || "-"}
      </Text>
    </HStack>
  );
};

export const StatusActive = ({ status }) => (
  <Box>
    {status ? (
      <Box
        bg={getStatusStyle("Active").bg}
        color={getStatusStyle("Active").color}
        borderColor={getStatusStyle("Active").border}
        borderWidth={1}
        borderRadius="full"
        px={2}
        py={0.5}
        textAlign="center"
        fontSize="xs"
        fontWeight="normal"
        display="inline-block"
        width="75px"
      >
        <FontAwesomeIcon
          icon={faCircleCheck}
          color={getStatusStyle("Active").color}
          size="sm"
        /> Active
      </Box>
    ) : (
      <Box
        bg={getStatusStyle("Inactive").bg}
        color={getStatusStyle("Inactive").color}
        borderColor={getStatusStyle("Inactive").border}
        borderWidth={1}
        borderRadius="full"
        px={2}
        py={0.5}
        textAlign="center"
        fontSize="xs"
        fontWeight="normal"
        display="inline-block"
        width="75px"
      >
        <FontAwesomeIcon
          icon={faCircleMinus}
          color={getStatusStyle("Inactive").color}
          size="sm"
        /> Inactive
      </Box>
    )}
  </Box>
);
